import type { FeedEntry } from '~/services/layout';
import { isRecord } from '~/services/layout/layout.ts';

export const AUDIO_PLAYER_SCREEN_TYPE = 'web_audio_player';
export const AUDIO_PLAYER_LINK_PREFIX = 'audio-player::';
export const AUDIO_PLAYER_OPEN_EVENT = 'web-audio-player:open';
export const AUDIO_PLAYER_CLOSE_EVENT = 'web-audio-player:close';
export const AUDIO_PLAYER_COLLAPSE_EXPANDED_EVENT =
  'web-audio-player:collapse-expanded';

export type AudioPlayerEventDetail = {
  entry: FeedEntry;
};

export function createAudioPlayerLink(entry: FeedEntry): string {
  return `${AUDIO_PLAYER_LINK_PREFIX}${encodeURIComponent(
    JSON.stringify({ entry })
  )}`;
}

export function parseAudioPlayerLink(
  link: string
): AudioPlayerEventDetail | null {
  try {
    if (!link.startsWith(AUDIO_PLAYER_LINK_PREFIX)) return null;

    const payload = link.slice(AUDIO_PLAYER_LINK_PREFIX.length);
    const parsed = JSON.parse(decodeURIComponent(payload));

    const entry = parsed?.entry as FeedEntry | undefined;

    if (!entry) {
      return null;
    }

    const fallbackHref =
      typeof entry.link?.href === 'string' && entry.link.href.length > 0
        ? entry.link.href
        : undefined;

    if (!entry.content?.src && fallbackHref) {
      entry.content = {
        src: fallbackHref,
        type: entry.content?.type || 'audio/mpeg',
      };
    }

    if (!entry?.content?.src) return null;

    return { entry };
  } catch (error) {
    return null;
  }
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function hasAudioTypeQueryParam(src: string): boolean {
  try {
    const url = new URL(src);
    const typeParam = url.searchParams.get('type');

    return typeof typeParam === 'string' && typeParam.startsWith('audio/');
  } catch {
    return /[?&]type=audio\//i.test(src);
  }
}

function isLikelyDirectPlayableAudioSource({
  src,
  type,
}: {
  src: string;
  type?: string;
}): boolean {
  const normalizedType = (type || '').toLowerCase();

  if (
    normalizedType === 'application/x-mpegurl' ||
    normalizedType === 'application/vnd.apple.mpegurl'
  ) {
    return true;
  }

  if (/\.(m3u8|mp3|aac|m4a|ogg|oga|opus|wav|flac)(?:$|\?)/i.test(src)) {
    return true;
  }

  if (hasAudioTypeQueryParam(src)) {
    return true;
  }

  return false;
}

function extractPlayableContentFromPayload(payload: unknown): {
  src: string;
  type?: string;
} | null {
  if (!isRecord(payload)) {
    return null;
  }

  const payloadEntry = payload.entry;
  let linkHref: string | undefined;

  if (Array.isArray(payloadEntry)) {
    for (const item of payloadEntry) {
      if (!isRecord(item)) {
        continue;
      }

      if (isRecord(item.content) && isNonEmptyString(item.content.src)) {
        return {
          src: item.content.src,
          type: isNonEmptyString(item.content.type)
            ? item.content.type
            : undefined,
        };
      }

      if (
        !linkHref &&
        isRecord(item.link) &&
        isNonEmptyString(item.link.href)
      ) {
        linkHref = item.link.href;
      }
    }
  }

  if (linkHref) {
    return { src: linkHref };
  }

  if (isRecord(payload.content) && isNonEmptyString(payload.content.src)) {
    return {
      src: payload.content.src,
      type: isNonEmptyString(payload.content.type)
        ? payload.content.type
        : undefined,
    };
  }

  if (isRecord(payload.link) && isNonEmptyString(payload.link.href)) {
    return { src: payload.link.href };
  }

  return null;
}

async function resolvePlaybackSource(
  entry: FeedEntry
): Promise<FeedEntry | null> {
  const contentSrc = entry?.content?.src || entry?.link?.href;

  if (!contentSrc) {
    return null;
  }

  if (
    isLikelyDirectPlayableAudioSource({
      src: contentSrc,
      type: entry.content?.type,
    })
  ) {
    if (entry.content?.src) {
      return entry;
    }

    return {
      ...entry,
      content: {
        src: contentSrc,
        type: entry.content?.type || 'audio/mpeg',
      },
    };
  }

  try {
    const response = await fetch(contentSrc);

    if (!response.ok) {
      return entry;
    }

    const resolvedContent = extractPlayableContentFromPayload(
      await response.json()
    );

    if (!resolvedContent?.src || resolvedContent.src === contentSrc) {
      return entry;
    }

    return resolvePlaybackSource({
      ...entry,
      content: {
        src: resolvedContent.src,
        type: resolvedContent.type || entry.content?.type || 'audio/mpeg',
      },
    });
  } catch {
    return entry;
  }
}

/**
 * Resolves an entry to a directly playable source.
 * Detail feeds often only contain a preload link, so that link is followed.
 */
export async function resolveAudioPlayerPlayableEntry(
  entry: FeedEntry
): Promise<FeedEntry | null> {
  return resolvePlaybackSource(entry);
}

export function dispatchAudioPlayerOpen(detail: AudioPlayerEventDetail): void {
  window.dispatchEvent(
    new CustomEvent(AUDIO_PLAYER_OPEN_EVENT, {
      detail,
    })
  );
}

export function dispatchAudioPlayerCollapseExpanded(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(AUDIO_PLAYER_COLLAPSE_EXPANDED_EVENT));
}

export function dispatchAudioPlayerClose(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(AUDIO_PLAYER_CLOSE_EVENT));
}

export function getIsAudioPlayerLink(link: string | undefined): boolean {
  return typeof link === 'string' && link.startsWith(AUDIO_PLAYER_LINK_PREFIX);
}
